import { PELL_ZONE_BAND_MIN_GAP, PELL_ZONE_BOUNDS, type Zone } from '../config/constants';
import { getSetting, setSetting } from '../settings/userSettings';

export type ZoneBounds = Record<Zone, readonly [number, number]>;

/**
 * User-tunable body-zone height bands (fraction of body height), shared by pell hit
 * classification and targets vertical spawn. The five contiguous zones are defined by the
 * four *internal* boundaries (bottom→top): leg|flank, flank|chest, chest|shoulder,
 * shoulder|head. The outer edges are fixed at 0 (leg bottom) and 1 (head top).
 */
const BOUNDARY_COUNT = 4;

const DEFAULT_BOUNDARIES: number[] = [
  PELL_ZONE_BOUNDS.leg[1],
  PELL_ZONE_BOUNDS.flank[1],
  PELL_ZONE_BOUNDS.chest[1],
  PELL_ZONE_BOUNDS.shoulder[1],
];

let boundaries = loadBoundaries();
let cachedBounds = boundsFromBoundaries(boundaries);

/** The four internal boundaries, bottom→top. Returns a defensive copy. */
export function getZoneBoundaries(): number[] {
  return boundaries.slice();
}

/** Current zone bands as a `Zone -> [low, high]` record (same shape as `PELL_ZONE_BOUNDS`). */
export function getZoneBands(): ZoneBounds {
  return cachedBounds;
}

/** Replace the boundaries (sanitized to stay ordered and within (0,1)) and persist. */
export function setZoneBoundaries(next: number[]): void {
  boundaries = sanitize(next);
  cachedBounds = boundsFromBoundaries(boundaries);
  save(boundaries);
}

function boundsFromBoundaries(b: number[]): ZoneBounds {
  const [b1, b2, b3, b4] = b as [number, number, number, number];
  return {
    leg: [0, b1],
    flank: [b1, b2],
    chest: [b2, b3],
    shoulder: [b3, b4],
    head: [b4, 1.0],
  };
}

/** Clamp each boundary so the sequence stays strictly ascending with a minimum gap. */
function sanitize(input: number[]): number[] {
  const gap = PELL_ZONE_BAND_MIN_GAP;
  const out: number[] = [];
  for (let i = 0; i < BOUNDARY_COUNT; i++) {
    const raw = Number(input[i]);
    const fallback = DEFAULT_BOUNDARIES[i]!;
    const v = isFinite(raw) ? raw : fallback;
    const lo = (out[i - 1] ?? 0) + gap;
    // Reserve room for the remaining boundaries plus the top edge.
    const hi = 1 - gap * (BOUNDARY_COUNT - i);
    out.push(Math.min(hi, Math.max(lo, v)));
  }
  return out;
}

function loadBoundaries(): number[] {
  const stored = getSetting('zoneBoundaries');
  if (!Array.isArray(stored) || stored.length !== BOUNDARY_COUNT) {
    return DEFAULT_BOUNDARIES.slice();
  }
  return sanitize(stored.map(Number));
}

function save(b: number[]): void {
  setSetting('zoneBoundaries', b.slice());
}

/** Re-read boundaries from the store (e.g. after a cloud settings pull). */
export function reloadZoneBoundaries(): void {
  boundaries = loadBoundaries();
  cachedBounds = boundsFromBoundaries(boundaries);
}
